// ============================================================
// BOOTH — what a payment actually COSTS the platform
// ------------------------------------------------------------
// BOOTH takes every payment as a DESTINATION CHARGE on the platform account.
// That means Stripe's processing fee is debited from BOOTH's balance, not the
// connected account's — `on_behalf_of` sets the merchant of record for the
// statement descriptor and the pricing basis, and nothing more. Confirmed
// against the live account on 2026-10-01: every BOOTH Express account reports
//
//     controller.fees.payer   = "application_express"
//     controller.losses.payments = "application"
//
// so BOOTH pays processing AND carries dispute liability on all of it.
//
// These constants exist so contribution can be modelled in tests and reporting
// without anyone re-deriving Stripe's rate card from memory. They are COSTS, not
// prices: nothing here is charged to a customer.
//
// Source for the Connect figures: platform balance transactions of type
// `stripe_fee`, which bill monthly and name themselves exactly:
//   "Connect (2026-08-01 - 2026-08-31): Active Account Billing"   -$2.00
//   "Connect (2026-08-01 - 2026-08-31): Account Volume Billing"   -$0.24
//   "Connect (2026-08-01 - 2026-08-31): Payout Fee"               -$0.25
// ============================================================

/** Card-not-present processing, debited from BOOTH's balance per charge. */
export const PROCESSING_PERCENT = 0.029
export const PROCESSING_FIXED_CENTS = 30

/**
 * Surcharges Stripe adds to the base rate. CURRENCY DOES NOT DETERMINE THESE:
 * a card issued outside the US paying in USD still attracts the international
 * surcharge, and a conversion adds on top of that. A USD-only product is not a
 * domestic-only product.
 *
 * ESTIMATES — Stripe's published US rate card, not yet reconciled against this
 * account's own charges (no international charge has settled on it to date).
 */
export const INTERNATIONAL_CARD_PERCENT = 0.015
export const CURRENCY_CONVERSION_PERCENT = 0.01

/**
 * Stripe Billing, charged on RECURRING payment volume. Both of BOOTH's
 * subscription products ride it: auto-renewing booth rent (Checkout in
 * subscription mode) and client memberships.
 *
 * CONFIRMED PRESENT, RATE ESTIMATED. The platform ledger carries
 * "Billing - Usage Fee" rows (2026-01-10 and 2026-01-12, $0.01 each) against
 * $1.00 subscription charges, so Billing is billed to BOOTH. At that size
 * rounding hides the rate — $0.005 and $0.01 both bill as a cent — so the 0.5%
 * below is Stripe's published Billing Starter rate, NOT a measured one.
 * Verifying it needs `invoice_read` / `subscription_read`, which the audit key
 * does not carry.
 */
export const BILLING_RECURRING_PERCENT = 0.005

/**
 * Connect costs.
 *
 * The volume fee is billed on PAYOUT volume — the money that actually leaves to
 * a bank account — not on charge volume and not on transfer volume. Verified:
 * acct_1SifjfElfuztdH8h took two $50 bookings in July 2026, was transferred
 * $100.00, paid $3.00 of application fees, and was paid out $97.00 on
 * 2026-08-09. August's bill read
 *
 *     Account Volume Billing  -$0.24     ( $97.00 x 0.25% = $0.2425 )
 *     Payout Fee              -$0.25     ( one payout )
 *
 * which pins the basis to the payout, not the $100.00 charged.
 */
export const CONNECT_VOLUME_PERCENT = 0.0025
export const CONNECT_PAYOUT_FEE_CENTS = 25 // per PAYOUT event, not per charge
export const CONNECT_ACTIVE_ACCOUNT_CENTS_PER_MONTH = 200 // per ACTIVE account

/** What Stripe deducts from a charge of `totalCents`, in cents. */
export function processingCostCents(
  totalCents: number,
  opts: { internationalCard?: boolean; currencyConversion?: boolean } = {},
): number {
  if (totalCents <= 0) return 0
  let percent = PROCESSING_PERCENT
  if (opts.internationalCard) percent += INTERNATIONAL_CARD_PERCENT
  if (opts.currencyConversion) percent += CURRENCY_CONVERSION_PERCENT
  return Math.round(totalCents * percent) + PROCESSING_FIXED_CENTS
}

/**
 * The 0.25% Connect volume fee attributable to one transaction.
 *
 * ESTIMATE. Stripe bills this monthly on an account's total payout volume, so
 * attributing it to a single charge assumes the recipient's net is eventually
 * paid out in full — true over a month, approximate for any one transaction.
 *
 * @param recipientCents what the connected account keeps, i.e. the payout basis
 */
export function connectVolumeCostCents(recipientCents: number): number {
  if (recipientCents <= 0) return 0
  return Math.round(recipientCents * CONNECT_VOLUME_PERCENT)
}

export interface Contribution {
  /** What the customer's card is charged, in cents. */
  customerTotalCents: number
  /** What lands with the shop or barber after BOOTH's fee, in cents. */
  recipientCents: number
  /** BOOTH's application fee before any cost, in cents. */
  grossPlatformFeeCents: number
  /** Stripe card processing, debited from BOOTH, in cents. */
  processingCents: number
  /** Stripe Billing on recurring volume, in cents. 0 for one-off charges. */
  billingCents: number
  /** Attributed Connect volume fee, in cents. ESTIMATE — see the function. */
  connectVolumeCents: number
  /**
   * Gross fee less every per-transaction cost. EXCLUDES account-level costs
   * (the $0.25 payout fee and the $2/month active-account fee) — pass this into
   * {@link modelAccountMonth} to get the figure that decides whether an account
   * is worth having.
   */
  transactionContributionCents: number
}

/**
 * Model one destination charge end to end.
 *
 * `recurring` adds Stripe Billing. `internationalCard` / `currencyConversion`
 * add their surcharges — both are possible on a USD charge.
 */
export function modelCharge(args: {
  customerTotalCents: number
  platformFeeCents: number
  recurring?: boolean
  internationalCard?: boolean
  currencyConversion?: boolean
}): Contribution {
  const { customerTotalCents, platformFeeCents, recurring = false } = args
  const recipientCents = customerTotalCents - platformFeeCents
  const processing = processingCostCents(customerTotalCents, args)
  const billing = recurring ? Math.round(customerTotalCents * BILLING_RECURRING_PERCENT) : 0
  const connectVolume = connectVolumeCostCents(recipientCents)
  return {
    customerTotalCents,
    recipientCents,
    grossPlatformFeeCents: platformFeeCents,
    processingCents: processing,
    billingCents: billing,
    connectVolumeCents: connectVolume,
    transactionContributionCents: platformFeeCents - processing - billing - connectVolume,
  }
}

export interface AccountMonth {
  transactionContributionCents: number
  payoutFeesCents: number
  activeAccountFeeCents: number
  /** What one connected account's month is actually worth to BOOTH. */
  accountMonthContributionCents: number
}

/**
 * Roll a month of one connected account up to the number that matters.
 *
 * A single transaction's contribution is not BOOTH's margin: an account costs
 * $2/month the moment it is active at all, plus $0.25 per payout. One cheap
 * booking in a month is loss-making however healthy its per-charge line looks.
 */
export function modelAccountMonth(args: {
  transactionContributionsCents: number[]
  payouts: number
}): AccountMonth {
  const txn = args.transactionContributionsCents.reduce((a, b) => a + b, 0)
  const payoutFees = Math.max(0, args.payouts) * CONNECT_PAYOUT_FEE_CENTS
  const active = args.transactionContributionsCents.length > 0 ? CONNECT_ACTIVE_ACCOUNT_CENTS_PER_MONTH : 0
  return {
    transactionContributionCents: txn,
    payoutFeesCents: payoutFees,
    activeAccountFeeCents: active,
    accountMonthContributionCents: txn - payoutFees - active,
  }
}
